import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

/**
 * Searchable customer dropdown.
 * Props:
 *   customers       — array of {id, name, phone, status, ...}
 *   value           — selected customer id (string)
 *   onChange(id)    — called with new id
 *   placeholder     — default "Select customer"
 *   showPhone       — bool, default true (shows phone in row + button)
 *   filter          — optional (c)=>bool to limit which customers show
 *   className       — extra class for trigger button
 *   accent          — "#00E054" | "#06B6D4" | "#FFD700" etc — focus color
 *   testid          — optional data-testid
 *   disabled        — bool
 */
export default function CustomerCombobox({
  customers = [],
  value,
  onChange,
  placeholder = "Select customer",
  showPhone = true,
  filter,
  className,
  accent = "#00E054",
  testid,
  disabled = false,
}) {
  const [open, setOpen] = React.useState(false);

  const list = React.useMemo(() => {
    const arr = filter ? customers.filter(filter) : customers;
    return arr.slice().sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
    );
  }, [customers, filter]);

  const selected = React.useMemo(
    () => list.find((c) => c.id === value) || customers.find((c) => c.id === value),
    [list, customers, value]
  );

  const label = selected
    ? showPhone && selected.phone
      ? `${selected.name} (${selected.phone})`
      : selected.name
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testid}
          className={cn(
            "w-full flex items-center justify-between bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-sm text-white outline-none transition-colors",
            "hover:border-[#3F3F46] focus:outline-none disabled:opacity-50",
            className
          )}
          style={open ? { borderColor: accent } : undefined}
        >
          <span className={cn("truncate", !selected && "text-gray-500")}>{label}</span>
          <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-[#0F0F0F] border-[#27272A] text-white"
        align="start"
        style={{ width: "var(--radix-popover-trigger-width)", minWidth: 240 }}
      >
        <Command
          className="bg-[#0F0F0F] text-white [&_[cmdk-input-wrapper]]:border-b [&_[cmdk-input-wrapper]]:border-[#27272A]"
          shouldFilter={true}
        >
          <CommandInput
            placeholder="Search by name or phone…"
            className="text-white placeholder:text-gray-400 caret-white"
          />
          <CommandList className="max-h-[260px]">
            <CommandEmpty className="py-4 text-center text-sm text-gray-400">
              No customer found.
            </CommandEmpty>
            <CommandGroup>
              {list.map((c) => {
                const isSel = c.id === value;
                const searchValue = `${c.name || ""} ${c.phone || ""}`.trim();
                return (
                  <CommandItem
                    key={c.id}
                    value={searchValue}
                    onSelect={() => {
                      onChange?.(c.id);
                      setOpen(false);
                    }}
                    className="cursor-pointer text-white data-[selected=true]:bg-[#1F1F22] data-[selected=true]:text-white aria-selected:bg-[#1F1F22] aria-selected:text-white"
                  >
                    <Check
                      size={14}
                      className={cn("mr-2 shrink-0", isSel ? "opacity-100" : "opacity-0")}
                      style={isSel ? { color: accent } : undefined}
                    />
                    <div className="flex flex-col min-w-0">
                      <span className="truncate text-white">{c.name}</span>
                      {showPhone && c.phone && (
                        <span className="text-xs text-gray-400 truncate">{c.phone}</span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
